/**
 * REST boundary for role escalation.
 *
 *   POST /v1/escalation-requests                      submit
 *   GET  /v1/escalation-requests                      list (self or tenant-wide)
 *   POST /v1/escalation-requests/{id}:decide          approve / deny
 *   POST /v1/escalation-requests/{id}:cancel          withdraw your own
 *   POST /v1/escalation-requests/{id}:revoke          end an approval early
 *   GET  /v1/escalation-approver-groups               read the approver list
 *   PUT  /v1/escalation-approver-groups               replace the approver list
 */
import { restGet, restPost, restPut, idForPath } from "./restClient.js";
import { providerToDbString, providerToWireEnum } from "./identityClient.js";
import { PERMISSIONS } from "./permissionKeys.js";

export const MAX_JUSTIFICATION_CHARS = 2000;

export const ESCALATABLE_ROLE_SLUG = "detection-engineer";
export const ESCALATABLE_ROLE_LABEL = "Detection engineer";

/**
 * Whether this caller is the one who asks rather than the one who decides.
 *
 * Holding the request permission is not enough: a tenant admin holds it because
 * the role holds every permission in the catalog. An approver filing a request
 * is a request nobody else can decide — the store refuses self-approval — so the
 * two sides are kept disjoint here.
 *
 * Approval authority also arrives through a directory group, which grants no
 * permission, so the flag is checked alongside the permission.
 *
 * Resolves to false while the posture is unknown, so the affordance appears once
 * rather than flashing and being withdrawn.
 */
export function canRequestEscalation(caps) {
    if (!caps?.available || !(caps.permissions instanceof Set)) return false;
    if (caps.isEscalationApprover) return false;
    return caps.permissions.has(PERMISSIONS.ESCALATIONS_REQUEST)
        && !caps.permissions.has(PERMISSIONS.ESCALATIONS_APPROVE);
}

/**
 * Whether an escalation may name a person at all.
 *
 * A target's subject axis is only enforced through the subject predicate, which
 * a `legacy_passthrough` tenant never emits: there an approval over a person
 * grants the permission and reaches no rows. Offering it would hand someone a
 * window that silently confers nothing, so the person-shaped shapes are hidden
 * until the tenant is on subject scoping.
 *
 * Unknown posture resolves to false, so the narrower machine-only flow is what
 * appears before the posture lands rather than an option that may vanish.
 */
export function canEscalateOnSubject(caps) {
    if (!caps?.available) return false;
    const disposition = caps.subjectScope?.disposition;
    return typeof disposition === "string"
        && disposition !== "legacy_passthrough"
        && disposition !== "unspecified";
}

export const WINDOW_PRESETS = Object.freeze([
    { seconds: 3600, label: "1 hour" },
    { seconds: 14400, label: "4 hours" },
    { seconds: 28800, label: "8 hours" },
    { seconds: 86400, label: "1 day" },
    { seconds: 259200, label: "3 days" },
    { seconds: 604800, label: "7 days" },
]);

export const ESCALATION_STATES = Object.freeze({
    PENDING: "pending",
    APPROVED: "approved",
    DENIED: "denied",
    CANCELLED: "cancelled",
    REVOKED: "revoked",
});

const STATE_WIRE = Object.freeze({
    pending: "ESCALATION_STATE_PENDING",
    approved: "ESCALATION_STATE_APPROVED",
    denied: "ESCALATION_STATE_DENIED",
    cancelled: "ESCALATION_STATE_CANCELLED",
    revoked: "ESCALATION_STATE_REVOKED",
});

function pick(obj, snake, camel) {
    if (!obj) return undefined;
    return obj[camel] ?? obj[snake];
}

function stamp(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
}

function stateOf(raw) {
    if (typeof raw !== "string") return ESCALATION_STATES.PENDING;
    const tail = raw.startsWith("ESCALATION_STATE_")
        ? raw.slice("ESCALATION_STATE_".length).toLowerCase()
        : raw.toLowerCase();
    return Object.values(ESCALATION_STATES).includes(tail) ? tail : ESCALATION_STATES.PENDING;
}

// A target names a person by their directory identity, not by the subject key
// the engine matches on: that key is derived from the tenant and the owning
// integration, so the server mints it and the client never guesses.
export function transformSubject(raw) {
    if (!raw) return null;
    const sourceId = pick(raw, "source_id", "sourceId") ?? "";
    if (!sourceId) return null;
    return {
        sourceProvider: pick(raw, "source_provider", "sourceProvider") ?? "",
        integrationId: pick(raw, "integration_id", "integrationId") ?? "",
        sourceId,
        // Carried only where a picker already had it. A request read back off
        // the wire has no name on it: `useSubjectNames` resolves those.
        displayName: pick(raw, "display_name", "displayName") ?? "",
    };
}

export function subjectKey(subject) {
    if (!subject) return "";
    return `${subject.sourceProvider} ${subject.integrationId} ${subject.sourceId}`;
}

export function subjectLabel(subject) {
    return subject?.displayName || subject?.sourceId || "";
}

function transformTarget(raw) {
    return {
        subject: transformSubject(pick(raw, "subject", "subject")),
        endpointId: pick(raw, "endpoint_id", "endpointId") ?? "",
    };
}

export function transformEscalationRequest(raw) {
    return {
        id: raw?.id ?? "",
        requesterUserId: pick(raw, "requester_user_id", "requesterUserId") ?? "",
        // Empty when the person has left the tenant; the id is all that is left.
        requesterDisplay: pick(raw, "requester_display", "requesterDisplay") ?? "",
        decidedByDisplay: pick(raw, "decided_by_display", "decidedByDisplay") ?? "",
        target: transformTarget(raw?.target),
        requestedRoleId: pick(raw, "requested_role_id", "requestedRoleId") ?? "",
        justification: raw?.justification ?? "",
        requestedSeconds: Number(pick(raw, "requested_seconds", "requestedSeconds") ?? 0),
        state: stateOf(raw?.state),
        createdAt: stamp(pick(raw, "created_at", "createdAt")),
        decidedByUserId: pick(raw, "decided_by_user_id", "decidedByUserId") ?? "",
        decidedAt: stamp(pick(raw, "decided_at", "decidedAt")),
        decisionNote: pick(raw, "decision_note", "decisionNote") ?? "",
        expiresAt: stamp(pick(raw, "expires_at", "expiresAt")),
        revokedByUserId: pick(raw, "revoked_by_user_id", "revokedByUserId") ?? "",
        revokedAt: stamp(pick(raw, "revoked_at", "revokedAt")),
    };
}

// There is no expired state on the wire: an approval whose window has elapsed
// stays `approved` with a past `expiresAt`.
export function isLiveEscalation(request, now = Date.now()) {
    return request?.state === ESCALATION_STATES.APPROVED
        && !request.revokedAt
        && request.expiresAt instanceof Date
        && request.expiresAt.getTime() > now;
}

// Revoking sets the state to `revoked` and keeps `decidedAt`, so an approval
// that was ended early is still an approval that happened: it opened a window,
// it may have read, and its trail is exactly what an investigation needs.
export function openedWindow(request) {
    return Boolean(request?.decidedAt)
        && (request.state === ESCALATION_STATES.APPROVED
            || request.state === ESCALATION_STATES.REVOKED);
}

export function targetShape(target) {
    if (target?.subject && target?.endpointId) return "person_on_machine";
    if (target?.subject) return "person";
    if (target?.endpointId) return "machine";
    return "none";
}

export async function listEscalationRequests({ states = [], limit = 0, pageToken = "" } = {}) {
    // One key per value; restGet's object form would join the array into a
    // single comma-separated value the gateway rejects.
    const qs = new URLSearchParams();
    for (const state of states) {
        const wire = STATE_WIRE[state];
        if (wire) qs.append("states", wire);
    }
    if (limit > 0) qs.append("limit", String(limit));
    if (pageToken) qs.append("page_token", pageToken);
    const suffix = qs.toString();
    const raw = await restGet(`/v1/escalation-requests${suffix ? `?${suffix}` : ""}`);
    return {
        requests: (raw?.requests ?? []).map(transformEscalationRequest),
        tenantWide: Boolean(pick(raw, "tenant_wide", "tenantWide")),
        nextPageToken: pick(raw, "next_page_token", "nextPageToken") ?? "",
    };
}

export async function createEscalationRequest({ subject, endpointId, justification, requestedSeconds }) {
    const target = {};
    if (subject) {
        target.subject = {
            // `EscalationSubject.source_provider` is a DirectoryProviderType,
            // so it takes the enum value name, not the normalized form the
            // directory list hands back.
            source_provider: providerToWireEnum(subject.sourceProvider),
            integration_id: subject.integrationId,
            source_id: subject.sourceId,
        };
    }
    if (endpointId) target.endpoint_id = endpointId;
    const raw = await restPost("/v1/escalation-requests", {
        target,
        justification,
        requested_seconds: requestedSeconds,
        requested_role_slug: ESCALATABLE_ROLE_SLUG,
    });
    return transformEscalationRequest(raw?.request);
}

export async function decideEscalationRequest({ requestId, approve, grantedSeconds = null, note = "" }) {
    const body = { approve };
    if (approve && grantedSeconds) body.granted_seconds = grantedSeconds;
    if (note) body.note = note;
    const raw = await restPost(`/v1/escalation-requests/${idForPath(requestId)}:decide`, body);
    return transformEscalationRequest(raw?.request);
}

export async function cancelEscalationRequest(requestId) {
    const raw = await restPost(`/v1/escalation-requests/${idForPath(requestId)}:cancel`, {});
    return transformEscalationRequest(raw?.request);
}

export async function revokeEscalationRequest(requestId) {
    const raw = await restPost(`/v1/escalation-requests/${idForPath(requestId)}:revoke`, {});
    return transformEscalationRequest(raw?.request);
}

function transformApproverGroup(raw) {
    const key = raw?.key ?? {};
    return {
        sourceProvider: pick(key, "source_provider", "sourceProvider") ?? "",
        integrationId: pick(key, "integration_id", "integrationId") ?? "",
        sourceId: pick(key, "source_id", "sourceId") ?? "",
        displayName: pick(raw, "display_name", "displayName") ?? "",
    };
}

export function approverGroupKey(group) {
    return `${group?.sourceProvider ?? ""} ${group?.integrationId ?? ""} ${group?.sourceId ?? ""}`;
}

export async function getEscalationApproverGroups() {
    const raw = await restGet("/v1/escalation-approver-groups");
    return (raw?.groups ?? []).map(transformApproverGroup);
}

export async function setEscalationApproverGroups(groups) {
    const raw = await restPut("/v1/escalation-approver-groups", {
        groups: groups.map((group) => ({
            // `DirectoryGroupKey.source_provider` is a plain string compared
            // byte-exactly against the stored snake_case, so the normalized
            // form would match no group and the write would be refused.
            source_provider: providerToDbString(group.sourceProvider),
            integration_id: group.integrationId,
            source_id: group.sourceId,
        })),
    });
    return (raw?.groups ?? []).map(transformApproverGroup);
}

/**
 * Every read one escalation carried, newest first. Open to whoever may decide
 * an escalation, which is what the Audit tab gates on.
 *
 * The trail carries no target and no content: every row shares the
 * escalation's own target, and only the names of the sensitive columns the
 * query referenced are recorded, never their values.
 */
export async function listEscalationReads({ requestId, pageSize = 0, pageToken = "" } = {}) {
    const qs = new URLSearchParams();
    if (pageSize > 0) qs.append("page_size", String(pageSize));
    if (pageToken) qs.append("page_token", pageToken);
    const suffix = qs.toString();
    const raw = await restGet(
        `/v1/escalation-requests/${idForPath(requestId)}/reads${suffix ? `?${suffix}` : ""}`,
    );
    return {
        reads: (raw?.reads ?? []).map((read) => ({
            occurredAt: stamp(pick(read, "occurred_at", "occurredAt")),
            callerDisplay: pick(read, "caller_display", "callerDisplay") ?? "",
            rpcName: pick(read, "rpc_name", "rpcName") ?? "",
            sensitiveColumns: pick(read, "sensitive_columns", "sensitiveColumns") ?? [],
        })),
        nextPageToken: pick(raw, "next_page_token", "nextPageToken") ?? "",
    };
}
